/**
 * Editable marketing / index page copy (titles, headings, CTAs, How It Works, 404, search).
 * Fields are shown or hidden by `page_key` (same pattern as Destinations × `level`).
 */
import { buildCollection } from "@firecms/core";
import { editorOrAdminPermissions } from "../permissions";
import { CONTENT_STATUS_ENUM } from "../lib/contentStatus";
import {
  STATUS_FIELD_DESCRIPTION,
  publishedSnapshotProperties,
} from "../lib/publishedSnapshotProperties";
import { collectPageCopyCompletenessIssues } from "../lib/completenessValidation";
import { localizedStringProperties } from "../lib/localeMaps";
import { createStatusPreSave } from "../lib/statusPreSave";
import { createPageCopyTranslateActions } from "../lib/siteContentTranslateActions";

const PAGE_KEYS = {
  home: "Home",
  destination_index: "Destination index",
  guides_index: "Guides index",
  articles_index: "Articles index",
  authors_index: "Authors index",
  not_found: "404 / Not found",
  search: "Search UI",
} as const;

type PageKey = keyof typeof PAGE_KEYS;

/** Marketing / index pages that share title, heading, body, CTA, featured, community. */
const MARKETING_KEYS: PageKey[] = [
  "home",
  "destination_index",
  "guides_index",
  "articles_index",
  "authors_index",
];

/** Core title/heading/body — marketing pages plus 404 and search docs. */
const CORE_COPY_KEYS: PageKey[] = [...MARKETING_KEYS, "not_found", "search"];

function pageKeyOf(values: Record<string, unknown>): string {
  return typeof values.page_key === "string" ? values.page_key : "";
}

/** Hide field unless `page_key` is in the allowed set. Keeps stored values when hidden.
 * When `page_key` is unset (new doc), keep fields visible so the form is not empty. */
function hideUnless(
  values: Record<string, unknown>,
  allowed: PageKey | readonly PageKey[],
): false | { hidden: true; clearOnDisabled: false } {
  const key = pageKeyOf(values);
  if (!key) return false;
  const ok = typeof allowed === "string"
    ? key === allowed
    : (allowed as readonly string[]).includes(key);
  return ok ? false : { hidden: true, clearOnDisabled: false };
}

type ValuesArg = { values: Record<string, unknown> };

/** Localized string map; optionally visible only for certain page keys. */
const loc = (
  name: string,
  opts?: { description?: string; when?: PageKey | readonly PageKey[] },
) => {
  const base = {
    name,
    dataType: "map" as const,
    description: opts?.description,
    properties: localizedStringProperties(),
  };
  if (!opts?.when) return base;
  return ({ values }: ValuesArg) => ({
    ...base,
    disabled: hideUnless(values, opts.when!),
  });
};

export const pageCopyCollection = buildCollection({
  id: "page_copy",
  name: "Page Copy",
  singularName: "Page Copy",
  path: "page_copy",
  icon: "Article",
  group: "Site",
  permissions: editorOrAdminPermissions,
  entityActions: createPageCopyTranslateActions(),
  callbacks: {
    onPreSave: createStatusPreSave({
      collectCompleteness: collectPageCopyCompletenessIssues,
      entityLabel: "page copy",
    }),
  },
  properties: {
    page_key: {
      name: "Page key",
      dataType: "string",
      enumValues: PAGE_KEYS,
      validation: { required: true },
    },
    status: {
      name: "Status",
      dataType: "string",
      enumValues: CONTENT_STATUS_ENUM,
      description: STATUS_FIELD_DESCRIPTION,
    },
    title: loc("Document title", {
      description: "HTML <title> (before brand suffix)",
      when: CORE_COPY_KEYS,
    }),
    heading: loc("Primary heading", { when: CORE_COPY_KEYS }),
    body: loc("Primary body", { when: CORE_COPY_KEYS }),
    cta_label: loc("CTA label", { when: MARKETING_KEYS }),
    cta_href: ({ values }: ValuesArg) => ({
      name: "CTA URL",
      dataType: "string" as const,
      disabled: hideUnless(values, MARKETING_KEYS),
    }),
    featured_heading: loc("Featured section heading", { when: MARKETING_KEYS }),
    featured_body: loc("Featured section body", { when: MARKETING_KEYS }),
    community_heading: loc("Community section heading", { when: MARKETING_KEYS }),
    community_body: loc("Community section body", { when: MARKETING_KEYS }),
    sustainability_message: loc("Sustainability ribbon", { when: MARKETING_KEYS }),

    how_it_works_heading: loc("How it works — heading", { when: "home" }),
    how_it_works_step1_title: loc("How it works — step 1 title", { when: "home" }),
    how_it_works_step1_body: loc("How it works — step 1 body", { when: "home" }),
    how_it_works_step2_title: loc("How it works — step 2 title", { when: "home" }),
    how_it_works_step2_body: loc("How it works — step 2 body", { when: "home" }),
    how_it_works_step3_title: loc("How it works — step 3 title", { when: "home" }),
    how_it_works_step3_body: loc("How it works — step 3 body", { when: "home" }),

    popular_heading: loc("Popular destinations heading", { when: "not_found" }),
    popular_aria: loc("Popular destinations aria-label", { when: "not_found" }),
    chip_boat: loc("404 chip — boat", { when: "not_found" }),
    chip_hiking: loc("404 chip — hiking", { when: "not_found" }),
    chip_lake: loc("404 chip — lake", { when: "not_found" }),
    chip_village: loc("404 chip — village", { when: "not_found" }),

    trigger_label: loc("Search trigger label", { when: "search" }),
    modal_title: loc("Search modal title", { when: "search" }),
    new_chat: loc("New chat button", { when: "search" }),
    close_search: loc("Close search aria-label", { when: "search" }),
    suggestions_aria: loc("Suggestions aria-label", { when: "search" }),
    suggestions_heading: loc("Suggestions heading", { when: "search" }),
    empty_state: loc("Search empty state", { when: "search" }),
    describe_journey: loc("Describe journey label", { when: "search" }),
    describe_placeholder: loc("Describe journey placeholder", { when: "search" }),
    send_message: loc("Send message aria-label", { when: "search" }),
    discover_aria: loc("Discover results aria-label", { when: "search" }),
    consulting: loc("Consulting loader text", { when: "search" }),
    search_not_configured: loc("Search not configured message", { when: "search" }),
    type_destination: loc("Discover type — destination", { when: "search" }),
    type_guide: loc("Discover type — guide", { when: "search" }),
    type_story: loc("Discover type — story", { when: "search" }),
    plan_stay: loc("Plan a stay fallback label", { when: "search" }),
    search_destinations_aria: loc("Search destinations aria-label", { when: "search" }),
    where_label: loc("Where label", { when: "search" }),
    where_placeholder: loc("Where placeholder", {
      description: "404 search field and search UI",
      when: ["not_found", "search"],
    }),
    chip_beach_label: loc("Chip — beach label", { when: "search" }),
    chip_beach_suggestion: loc("Chip — beach suggestion text", { when: "search" }),
    chip_alps_label: loc("Chip — Alps label", { when: "search" }),
    chip_alps_suggestion: loc("Chip — Alps suggestion text", { when: "search" }),
    chip_culture_label: loc("Chip — culture label", { when: "search" }),
    chip_culture_suggestion: loc("Chip — culture suggestion text", { when: "search" }),
    chip_sustain_label: loc("Chip — sustainable label", { when: "search" }),
    chip_sustain_suggestion: loc("Chip — sustainable suggestion text", { when: "search" }),
    chip_beach_mask_suggestion: loc("Chip — beach mask suggestion", { when: "search" }),
    booking_title: loc("Booking form title", { when: "search" }),
    booking_home_lead: loc("Booking form lead (home)", { when: "search" }),
    booking_full_lead: loc("Booking form lead (full)", { when: "search" }),
    booking_submit: loc("Booking submit label", { when: "search" }),
    departure: loc("Departure label", { when: "search" }),
    destination_label: loc("Destination field label", { when: "search" }),
    where_from: loc("Where from placeholder", { when: "search" }),
    where_to: loc("Where to placeholder", { when: "search" }),
    check_in: loc("Check-in label", { when: "search" }),
    check_out: loc("Check-out label", { when: "search" }),
    adults: loc("Adults label", { when: "search" }),
    children: loc("Children label", { when: "search" }),
    child_ages: loc("Children’s ages label", { when: "search" }),
    preferences: loc("Preferences label", { when: "search" }),
    preferences_placeholder: loc("Preferences placeholder", { when: "search" }),
    transit_aria: loc("Transit mode aria-label", { when: "search" }),
    train: loc("Train label", { when: "search" }),
    flight: loc("Flight label", { when: "search" }),
    ferry: loc("Ferry label", { when: "search" }),
    edit_search: loc("Edit search aria-label", { when: "search" }),

    ...publishedSnapshotProperties,
    created_at: {
      name: "Created At",
      dataType: "date",
      autoValue: "on_create",
    },
    updated_at: {
      name: "Updated At",
      dataType: "date",
      autoValue: "on_update",
    },
  },
});
